// src/store/useBulkTaskStore.ts
import { JSONContent } from "@tiptap/core";
import { StatusData, StatusGroupData } from "src/api/General/status-groups";
import {
  PriorityData,
  relativeDueDateUnits,
} from "src/api/Services/Journeys/journeyTemplates";
import { ProjectData } from "src/api/Services/Projects/projects";
import { TaskerData } from "src/api/Services/Tasks/tasks";
import { v4 as uuidv4 } from "uuid";
import { create } from "zustand";
import { immer } from "zustand/middleware/immer";

// Base type definitions that can be reused
type RelativeDueDateUnit = (typeof relativeDueDateUnits)[number];

// Common reusable types from your structure
type Duration = {
  unit: RelativeDueDateUnit;
  value: number;
};

type Dependency = {
  _id?: string;
  type: "task" | "stage" | "futureTask";
};

type Assignee = TaskerData;

type Variable = string;

export type TiptapNode = JSONContent;

// The common fields shared between Task and Subtask
export type TaskBase = {
  _id: string;
  title?: TiptapNode;
  description: string;
  dueDate?: string;
  duration?: Duration;
  dependency?: Dependency;
  assignee?: Assignee;
  status?: StatusData;
  priority?: PriorityData;
  stageId?: string;
  isVisible: boolean;
  isAddingDependency: boolean;
  relativeDueDate: Duration;
  dependentOn: Dependency;
  customFields?: BulkTaskCustomFieldValue[];
};


export type BulkTaskTextCustomFieldValue = {
  _id: string;
  type: "text";
  value: TiptapNode | null;
}

export type BulkTaskSelectCustomFieldValue = {
  _id: string;
  type: "select";
  value: string | null;
}

export type BulkTaskCustomFieldValue = BulkTaskTextCustomFieldValue
| BulkTaskSelectCustomFieldValue



// Complete Task type that includes the subtasks
type Task = TaskBase & {
  subtasks?: TaskBase[]; // Reusing TaskBase for subtasks
};

// Store state
interface BulkTaskState {
  isOpen: boolean;
  step: number;
  project?: ProjectData;
  statusGroup?: StatusGroupData;
  task: Task;
  isValid: boolean;
  isDirty: boolean;
  variables: Variable[];
  relativeDueDate?: Duration;
  dependentOn?: Dependency;

  actions: {
    // Setters
    setTitle: (title: TiptapNode) => void;
    setDescription: (description: string) => void;
    setDueDate: (dueDate?: string) => void;
    setDuration: (duration?: Duration) => void;
    setDependency: (dependency?: Dependency) => void;
    setAssignee: (assignee?: Assignee) => void;
    setStatus: (status?: StatusData) => void;
    setPriority: (priority?: PriorityData) => void;
    setStageId: (stageId?: string) => void;
    setIsVisible: (isVisible: boolean) => void;
    setIsOpen: (isOpen: boolean) => void;
    setIsAddingDependency: (isAddingDependency: boolean) => void;
    setRelativeDueDate: (relativeDueDate: Duration) => void;
    setDependentOn: (dependentOn: Dependency) => void;
    setCustomField: (customField: BulkTaskCustomFieldValue) => void;
    removeCustomField: (customFieldId: string) => void;

    // Project operations
    setProject: (project?: ProjectData) => void;
    setStatusGroup: (statusGroup?: StatusGroupData) => void;

    // Variable operations
    addVariable: (variable: Variable) => void;
    updateVariable: (index: number, variable: Partial<Variable>) => void;
    removeVariable: (index: number) => void;

    // Subtask operations
    addSubtask: (subtask: TaskBase) => void;
    updateSubtask: (_id: string, subtask: Partial<TaskBase>) => void;
    removeSubtask: (_id: string) => void;

    // Batch operations
    updateTask: (taskData: Partial<Task>) => void;
    reset: () => void;

    // Step operations
    setStep: (step: number) => void;

    // Validation
    validateTask: () => boolean;
  };
}

export const createDefaultTask = (): Task => ({
  _id: uuidv4(),
  description: "",
  isVisible: true,
  subtasks: [],
  isAddingDependency: false,
  relativeDueDate: { unit: "day", value: 1 },
  dependentOn: { type: "task", _id: "" },
});

// Create the store
const useBulkTaskStore = create<BulkTaskState>()(
  immer((set, get) => ({
    task: createDefaultTask(),
    isValid: false,
    isDirty: false,
    variables: [],
    isOpen: false,
    step: 1,
    actions: {
      // Setters for individual fields
      setIsOpen: (isOpen) =>
        set((state) => {
          state.isOpen = isOpen;
          if (!isOpen) {
            state.task = createDefaultTask();
            state.variables = [];
            state.step = 1;
            state.project = undefined;
            state.statusGroup = undefined;
            state.isDirty = false;
            state.isValid = false;
          }
        }),
      setTitle: (title) =>
        set((state) => {
          state.task.title = title;
          // state.isDirty = true;
          // state.isValid = get().actions.validateTask();
        }),
      setIsAddingDependency: (isAddingDependency) =>
        set((state) => {
          state.task.isAddingDependency = isAddingDependency;
        }),
      setDescription: (description) =>
        set((state) => {
          state.task.description = description;
          state.isDirty = true;
        }),

      setDueDate: (dueDate) =>
        set((state) => {
          state.task.dueDate = dueDate;
          state.isDirty = true;
        }),

      setDuration: (duration) =>
        set((state) => {
          state.task.duration = duration;
          state.isDirty = true;
        }),

      setDependency: (dependency) =>
        set((state) => {
          state.task.dependency = dependency;
          state.isDirty = true;
        }),

      setAssignee: (assignee) =>
        set((state) => {
          state.task.assignee = assignee;
          state.isDirty = true;
        }),

      setStatus: (status) =>
        set((state) => {
          state.task.status = status;
          state.isDirty = true;
        }),

      setPriority: (priority) =>
        set((state) => {
          state.task.priority = priority;
          state.isDirty = true;
        }),

      setStageId: (stageId) =>
        set((state) => {
          state.task.stageId = stageId;
          state.isDirty = true;
        }),

      setIsVisible: (isVisible) =>
        set((state) => {
          state.task.isVisible = isVisible;
          state.isDirty = true;
        }),

      setRelativeDueDate: (relativeDueDate) =>
        set((state) => {
          state.task.relativeDueDate = relativeDueDate;
          state.isDirty = true;
        }),

      setDependentOn: (dependentOn) =>
        set((state) => {
          state.task.dependentOn = dependentOn;
          state.isDirty = true;
        }),
      setCustomField: (customField) =>
        set((state) => {
          const existingFieldIndex = state.task.customFields?.findIndex(field => field._id === customField._id);
          if (existingFieldIndex === -1 || existingFieldIndex === undefined) {
            state.task.customFields = [...(state.task.customFields || []), customField];
          } else {
            state.task.customFields![existingFieldIndex] = customField;
          }
          state.isDirty = true;
        }),
      removeCustomField: (customFieldId) =>
        set((state) => {
          state.task.customFields = state.task.customFields?.filter(field => field._id !== customFieldId);
          state.isDirty = true;
        }),

      setProject: (project) =>
        set((state) => {
          state.project = project;
          state.isDirty = true;
        }),

      setStatusGroup: (statusGroup) =>
        set((state) => {
          state.statusGroup = statusGroup;
          state.isDirty = true;
        }),

      // Variable operations
      addVariable: (variable) =>
        set((state) => {
          state.variables.push(variable);
          state.isDirty = true;
        }),

      updateVariable: (index, variable) =>
        set((state) => {
          if (index >= 0 && index < state.variables.length) {
            Object.assign(state.variables[index], variable);
            state.isDirty = true;
          }
        }),

      removeVariable: (index) =>
        set((state) => {
          if (index >= 0 && index < state.variables.length) {
            state.variables.splice(index, 1);
            state.isDirty = true;
          }
        }),

      // Subtask operations
      addSubtask: (subtask) =>
        set((state) => {
          if (!state.task.subtasks) {
            state.task.subtasks = [];
          }
          state.task.subtasks.push(subtask);
          state.isDirty = true;
        }),

      updateSubtask: (_id, subtaskData) =>
        set((state) => {
          if (
            state.task.subtasks &&
            state.task.subtasks.some((subtask) => subtask._id === _id)
          ) {
            const index = state.task.subtasks.findIndex(
              (subtask) => subtask._id === _id
            );
            Object.assign(state.task.subtasks[index], subtaskData);
            state.isDirty = true;
          }
        }),

      removeSubtask: (_id) =>
        set((state) => {
          if (
            state.task.subtasks &&
            state.task.subtasks.some((subtask) => subtask._id === _id)
          ) {
            const index = state.task.subtasks.findIndex(
              (subtask) => subtask._id === _id
            );
            state.task.subtasks.splice(index, 1);
            state.isDirty = true;
          }
        }),

      // Step operations
      setStep: (step) =>
        set((state) => {
          state.step = step;
        }),

      // Batch operations
      updateTask: (taskData) =>
        set((state) => {
          state.task = {
            ...state.task,
            ...taskData,
          };
          state.isDirty = true;
          state.isValid = get().actions.validateTask();
        }),

      reset: () =>
        set((state) => {
          state.task = createDefaultTask();
          state.variables = [];
          state.step = 1;
          state.project = undefined;
          state.statusGroup = undefined;
          state.isDirty = false;
          state.isValid = false;
        }),

      // Validation
      validateTask: () => {
        // const { task } = get();
        // Basic validation: require title at minimum
        // const isValid = task.title.trim().length > 0;
        // set((state) => {
        //   state.isValid = isValid;
        // });
        return true;
      },
    },
  }))
);

export default useBulkTaskStore;

// Selector hooks for performance optimization
export const useIsOpen = () => useBulkTaskStore((state) => state.isOpen);
export const useTaskId = () => useBulkTaskStore((state) => state.task._id);
export const useTaskTitle = () => useBulkTaskStore((state) => state.task.title);
export const useTaskCustomFields = () =>
  useBulkTaskStore((state) => state.task.customFields);
export const useTaskDescription = () =>
  useBulkTaskStore((state) => state.task.description);
export const useTaskVariables = () =>
  useBulkTaskStore((state) => state.variables);
export const useTaskSubtasks = () =>
  useBulkTaskStore((state) => state.task.subtasks || []);
export const useTaskIsValid = () => useBulkTaskStore((state) => state.isValid);
export const useTaskIsDirty = () => useBulkTaskStore((state) => state.isDirty);
export const useTaskStep = () => useBulkTaskStore((state) => state.step);
// Additional selectors for common use cases
export const useTaskRelativeDueDate = () =>
  useBulkTaskStore((state) => state.task.relativeDueDate);
export const useTaskDependentOn = () =>
  useBulkTaskStore((state) => state.task.dependentOn);
export const useTaskAddingDependency = () =>
  useBulkTaskStore((state) => state.task.isAddingDependency);
export const useTaskProject = () => useBulkTaskStore((state) => state.project);
export const useTaskStatus = () =>
  useBulkTaskStore((state) => state.task.status);
export const useTaskStatusGroup = () =>
  useBulkTaskStore((state) => state.statusGroup);
export const useTaskPriority = () =>
  useBulkTaskStore((state) => state.task.priority);
export const useTaskDueDate = () =>
  useBulkTaskStore((state) => state.task.dueDate);
export const useTaskActions = () => useBulkTaskStore((state) => state.actions);
export const useTaskStageId = () =>
  useBulkTaskStore((state) => state.task.stageId);
export const useTaskAssignee = () =>
  useBulkTaskStore((state) => state.task.assignee);
export const useTaskIsVisible = () =>
  useBulkTaskStore((state) => state.task.isVisible);
